import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Loader2,
  Lock,
  PenLine,
  Sun,
  CircleCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ApiError, getStep8Rows, getStepsOverview, type StepOverviewItem } from "@/lib/api";
import { shortDate } from "@/lib/format";

const WORDS = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve"];

// One calm line per step for the current-step card (owner copy, same voice as
// the catalog intro; falls back to the principle alone).
const BLURB: Record<number, string> = {
  1: "Looking honestly at powerlessness and what's become unmanageable.",
  2: "Coming to believe you don't have to do this on your own. Worked at your own pace, with your sponsor.",
  3: "Making a decision to stop running everything yourself, and to trust something beyond you — however you understand that.",
  4: "A written inventory, done a row at a time over as many sittings as it takes. Encrypted, and only yours.",
  5: "Happens in person, with your sponsor. There's nothing to write here.",
  6: "Becoming ready to be free of the patterns you've been carrying. Nothing to fix today — this step is about willingness.",
  7: "Step 6 was becoming ready. Step 7 is the asking — that what you've been carrying be lifted. Nothing to perform, and nothing to get right.",
  8: "A list of the people you've harmed, built one at a time, and becoming willing to make it right. Encrypted, and only yours.",
  9: "Making direct amends to the people on your list, except where it would injure them or others. Worked out with your sponsor, over as long as it takes.",
};

// Steps ahead that want their own line rather than a bare number (Step 9
// canvas): Step 10 is the daily check-in the person is already keeping.
const AHEAD_LABEL: Record<number, { label: string; note: string }> = {
  10: {
    label: "Step 10 — Daily inventory",
    note: "Your daily check-in. Nothing new to start.",
  },
};

// The just-opened sun hint, where a step wants its own words (Step 4 canvas).
const OPEN_HINT: Record<number, string> = {
  4: "Step 4 is open now. It's the long one — you'll write it in pieces.",
};

const PHASE_LABEL: Record<string, string> = {
  inventory: "your inventory",
  list: "your list",
  amends: "your amends",
  overview: "the Overview",
  reading: "the Reading",
  reflection: "Reflection",
  willingness: "Willingness",
  sponsor: "the sponsor hand-off",
  close: "the close",
};

/**
 * "You left off at …" — mid-sentence, so a step that renames the fourth phase
 * (Step 7 calls it "The asking") gets its own word, lower-cased to fit.
 */
function phaseLabel(s: StepOverviewItem): string {
  if (s.current_phase === "willingness" && s.willingness_label) {
    return s.willingness_label.charAt(0).toLowerCase() + s.willingness_label.slice(1);
  }
  return PHASE_LABEL[s.current_phase ?? ""] ?? "where you stopped";
}

export default function StepsOverview() {
  const navigate = useNavigate();
  const [steps, setSteps] = useState<StepOverviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Step 9 works from the Step 8 list, so its card says so plainly when that
  // list is empty rather than sending someone into an empty ceremony (Step 9
  // PRD §2). Only the row count is needed — the rows stay encrypted.
  const [step8Empty, setStep8Empty] = useState(false);

  useEffect(() => {
    getStepsOverview()
      .then(setSteps)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) navigate("/signin");
        else setError("Couldn't load your steps.");
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => {
    const s9 = steps.find((s) => s.step_number === 9);
    if (!s9 || (s9.state !== "available" && s9.state !== "in_progress")) return;
    getStep8Rows()
      .then((r) => setStep8Empty(r.rows.length === 0))
      .catch(() => {});
  }, [steps]);

  const lastCatalog = steps.reduce((m, s) => Math.max(m, s.step_number), 0);
  const ahead = Array.from({ length: Math.max(0, 12 - lastCatalog) }, (_, i) => lastCatalog + 1 + i);
  // "Open now" hint: a step past 1 that just became available (no work yet).
  const justOpened = steps.find((s) => s.step_number > 1 && s.state === "available");

  return (
    <div className="pb-16">
      <div className="flex items-center gap-2 px-4 pb-2 pt-6">
        <Button variant="ghost" size="icon" aria-label="Back" onClick={() => navigate("/app")}>
          <ArrowLeft className="h-5 w-5" strokeWidth={2} />
        </Button>
        <h1 className="flex-1 text-lg font-semibold text-foreground">Steps</h1>
      </div>

      <div className="flex flex-col gap-3 px-6 pt-1">
        <p className="text-[13px] leading-relaxed text-muted-foreground">
          Worked in order, with your sponsor. There's no clock on any of this.
        </p>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
            <span className="text-sm">Loading…</span>
          </div>
        ) : error ? (
          <p className="py-16 text-center text-sm text-muted-foreground">{error}</p>
        ) : (
          <>
            {steps.map((s) => {
              const title = `Step ${s.step_number}${s.principle ? ` — ${s.principle}` : ""}`;
              if (s.state === "ready" || s.state === "worked") {
                return (
                  <Card interactive key={s.step_number} onClick={() => navigate(`/steps/${s.step_number}`)}>
                    <CardContent className="flex items-center gap-3 px-4 py-3.5">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                      </span>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-foreground">{title}</p>
                        <p className="text-xs text-muted-foreground">
                          {s.state === "worked" ? "Worked" : "Marked ready"}
                          {s.updated_at ? ` ${shortDate(s.updated_at)}` : ""}
                          {/* Step 4: say plainly when the writing was deleted. */}
                          {s.wiped_at
                            ? ` · writing deleted ${shortDate(s.wiped_at)}`
                            : " · you can revisit anytime"}
                        </p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" strokeWidth={2} />
                    </CardContent>
                  </Card>
                );
              }
              if (s.state === "locked") {
                return (
                  <Card key={s.step_number}>
                    <CardContent className="flex flex-col gap-2 p-5">
                      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                        <Lock className="h-3 w-3" strokeWidth={2} /> Step {WORDS[s.step_number]} · Not yet
                      </p>
                      <h2 className="text-lg font-bold text-muted-foreground">Step {s.step_number}</h2>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        Opens when you mark Step {s.step_number - 1} ready. There's no rush to get there.
                      </p>
                    </CardContent>
                  </Card>
                );
              }
              // Step 9 with an empty Step 8 list: point back, don't invite
              // someone into a step that has nothing to work on (§2).
              if (s.step_number === 9 && step8Empty) {
                return (
                  <Card key={s.step_number} interactive onClick={() => navigate("/steps/8")}>
                    <CardContent className="flex flex-col gap-2.5 p-5">
                      <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                        Step Nine{s.principle ? ` · ${s.principle}` : ""}
                      </p>
                      <h2 className="text-lg font-bold text-foreground">{title}</h2>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        Your Step 8 list is empty, so there's nothing here yet. Add someone there
                        and this opens up.
                      </p>
                      <Button variant="outline" size="lg" fullWidth className="mt-1">
                        Go to Step 8
                      </Button>
                    </CardContent>
                  </Card>
                );
              }
              // available / in_progress — the current step's full card.
              const inprog = s.state === "in_progress";
              return (
                <div key={s.step_number} className="flex flex-col gap-2">
                  {justOpened?.step_number === s.step_number && (
                    <p className="mx-1 flex items-center gap-1.5 text-xs leading-relaxed text-muted-foreground">
                      <Sun className="h-3 w-3 shrink-0 text-primary" strokeWidth={2} />
                      {OPEN_HINT[s.step_number] ??
                        `Step ${s.step_number} is open now — whenever you're ready.`}
                    </p>
                  )}
                  <Card interactive onClick={() => navigate(`/steps/${s.step_number}`)}>
                    <CardContent className="flex flex-col gap-2.5 p-5">
                      <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                        Step {WORDS[s.step_number]} · Where you are
                      </p>
                      <h2 className="text-lg font-bold text-foreground">{title}</h2>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        {BLURB[s.step_number] ?? "Worked at your own pace, with your sponsor."}
                      </p>
                      {inprog && (
                        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <PenLine className="h-3 w-3" strokeWidth={2} />
                          You left off at {phaseLabel(s)}
                        </p>
                      )}
                      <Button size="lg" fullWidth className="mt-1">
                        {inprog ? `Continue Step ${s.step_number}` : `Begin Step ${s.step_number}`}
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              );
            })}

            {ahead.length > 0 && (
              <div className="mt-2">
                <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                  Ahead
                </p>
                <div className="mt-1">
                  {ahead.map((n) => (
                    <div
                      key={n}
                      className={`flex items-center gap-3 px-1 py-2 ${
                        AHEAD_LABEL[n] ? "opacity-85" : "opacity-55"
                      }`}
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-[13px] font-semibold text-muted-foreground">
                        {n}
                      </span>
                      <div>
                        <span className="text-sm text-muted-foreground">
                          {AHEAD_LABEL[n]?.label ?? `Step ${n}`}
                        </span>
                        {AHEAD_LABEL[n] && (
                          <p className="text-xs text-muted-foreground">{AHEAD_LABEL[n].note}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Each step opens when you get there. One at a time.
                </p>
              </div>
            )}
            <p className="mt-1 flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
              <CircleCheck className="h-3 w-3" strokeWidth={2} />
              Everything you write in a step is private unless you share it.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
